/**
 * The "DM an agent" composer — v1 = L1, stated honestly (spec #44 §2).
 *
 * There is no per-agent chat endpoint and no message table, so this does
 * not pretend to be a chat. It posts to `POST /api/ask`: the question
 * becomes a typed `MetricQuery`, the semantic layer computes, and the
 * answer text is rendered deterministically — the model cannot author a
 * number. The header line says so, and says that nothing is saved.
 *
 * Three edges, all honest:
 *   `no_matching_metric` → "I can't answer that from your data yet" plus a
 *      deep link to this agent's beliefs. Never a prose guess.
 *   402 → the composer is REPLACED by the plan's own reason.
 *   `resolved_query` → behind a disclosure, as the Ask page already does.
 */

import { useState } from 'react';
import { Button, Collapsible, InlineStack, Text, TextField } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import { useWorkspace } from '../../hooks/useWorkspace';
import type { AskTurn } from '../../types/workspace';
import { WorkspaceEmpty } from './WorkspaceEmpty';
import styles from './workspace.module.css';

function AskTurnView({ turn, domain }: { turn: AskTurn; domain: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.askTurn} data-testid={`ask-turn-${turn.id}`}>
      <Text as="p" fontWeight="semibold">
        {turn.question}
      </Text>
      {turn.error_code === 'no_matching_metric' ? (
        <Text as="p" tone="subdued">
          I can’t answer that from your data yet.{' '}
          <Link to={`/beliefs?domain=${domain}`}>this agent’s beliefs</Link>{' '}
          say what it is testing.
        </Text>
      ) : turn.error_message ? (
        <Text as="p" tone="subdued">
          {turn.error_message}
        </Text>
      ) : turn.answer_md ? (
        <>
          <Text as="p">{turn.answer_md}</Text>
          {turn.resolved_query ? (
            <>
              <Button
                variant="plain"
                size="slim"
                onClick={() => setOpen((v) => !v)}
                ariaExpanded={open}
                ariaControls={`resolved-${turn.id}`}
              >
                the query behind this number
              </Button>
              <Collapsible open={open} id={`resolved-${turn.id}`}>
                <pre className={styles.mono}>
                  {JSON.stringify(turn.resolved_query, null, 2)}
                </pre>
              </Collapsible>
            </>
          ) : null}
        </>
      ) : (
        <Text as="p" tone="subdued">
          Asking your warehouse…
        </Text>
      )}
    </div>
  );
}

export function Composer({ domain }: { domain: string }) {
  const { askAgent, askTurnsFor, askPending, askDisabledReason } =
    useWorkspace();
  const [question, setQuestion] = useState('');
  const turns = askTurnsFor(domain);

  return (
    <div className={styles.composer}>
      {turns.map((turn) => (
        <AskTurnView key={turn.id} turn={turn} domain={domain} />
      ))}
      {askDisabledReason ? (
        <WorkspaceEmpty kind="ask-not-entitled" reason={askDisabledReason} />
      ) : (
        <>
          <div className={styles.composerNote}>
            Answers come from your warehouse. Messages aren’t saved yet.
          </div>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Ask this agent"
                labelHidden
                autoComplete="off"
                placeholder="Ask about a metric this agent owns"
                value={question}
                onChange={setQuestion}
              />
            </div>
            <Button
              variant="primary"
              loading={askPending}
              onClick={() => {
                const text = question;
                setQuestion('');
                void askAgent(domain, text);
              }}
            >
              Ask
            </Button>
          </InlineStack>
        </>
      )}
    </div>
  );
}
